import { noop } from '@openx/utils/noop';
import { useCallback, useMemo } from 'react';
import { ContextualSegmentsList } from './components/ContextualSegmentsList';
import { SelectedContextualSegmentItemDisplay } from './drawer/components/SelectedContextualSegmentItemDisplay';
import { type EnrichedProviderSegments, type EnrichedSegment, removeEnrichedSegment } from './utils/enrichment';

export interface ContextualSegmentsReadOnlyItemsProps {
  enrichedSegments: EnrichedSegment[];
  readonly?: boolean;
  onChange?: (updater: React.SetStateAction<EnrichedProviderSegments[]>) => void;
}

export const ContextualSegmentsReadOnlyItems = ({
  enrichedSegments,
  onChange = noop,
  readonly,
}: ContextualSegmentsReadOnlyItemsProps) => {
  const { includes, excludes } = useMemo(() => {
    return enrichedSegments.reduce(
      (acc, segment) => {
        if (segment.inclusionType === 'includes') {
          acc.includes.push(segment);
        } else {
          acc.excludes.push(segment);
        }
        return acc;
      },
      { includes: [] as EnrichedSegment[], excludes: [] as EnrichedSegment[] }
    );
  }, [enrichedSegments]);

  const selections = useMemo(
    () =>
      enrichedSegments.reduce((result, segment) => {
        let selection = result.find(item => item.providerId === segment.providerId);
        if (!selection) {
          selection = { providerId: segment.providerId, includes: [], excludes: [] };
          result.push(selection);
        }
        selection[segment.inclusionType].push(segment.metadata);
        return result;
      }, [] as EnrichedProviderSegments[]),
    [enrichedSegments]
  );

  const handleRemove = useCallback(
    (segment: EnrichedSegment) => {
      const nextSelections = removeEnrichedSegment(selections, segment.id, segment.providerId);
      onChange(nextSelections);
    },
    [onChange, selections]
  );

  return (
    <ContextualSegmentsList
      includes={includes}
      excludes={excludes}
      onRemove={handleRemove}
      readonly={readonly}
      ItemComponent={SelectedContextualSegmentItemDisplay}
    />
  );
};
